import { createStore, type StoreApi } from "zustand/vanilla";
import { useStore } from "zustand";
import { api as realApi } from "./http";
import { realtime as realRt, type Realtime } from "./realtime";
import { chat, chatStore } from "./chatStore";
import { JoinError, SfuSession, mediaConstraints, mediaErrorText, type EndReason, type JoinInfo, type RosterPeer, type SfuEvents } from "./sfu";
import type { Frame } from "./types";

export type CallDirection = "incoming" | "outgoing" | "missed";

/** A row of call history (kept on this device: the backend has no personal call log yet). */
export interface CallRecord {
  id: string;
  contactId: string;
  name: string;
  direction: CallDirection;
  at: number;
  seconds: number;
  read: boolean;
}

export type CallPhase = "idle" | "starting" | "ringing" | "connecting" | "connected";

export interface ActiveCall {
  convId: string;
  contactId: string;
  name: string;
  direction: "incoming" | "outgoing";
  video: boolean;
  startedAt: number;
  connectedAt: number | null;
  muted: boolean;
  hold: boolean;
  camOn: boolean;
}

export interface IncomingCall {
  convId: string;
  contactId: string;
  name: string;
  at: number;
}

export interface CallState {
  phase: CallPhase;
  active: ActiveCall | null;
  incoming: IncomingCall | null;
  peers: RosterPeer[];
  streams: MediaStream[];
  localStream: MediaStream | null;
  history: CallRecord[];
  error: string;
}

export interface CallDeps {
  api: typeof realApi;
  rt: Pick<Realtime, "onFrame">;
  now?: () => number;
  getMedia?: (c: MediaStreamConstraints) => Promise<MediaStream>;
  newSession?: (ev: SfuEvents) => Pick<SfuSession, "join" | "leave" | "setMicEnabled" | "setCameraEnabled" | "localStream">;
  openDM: (userId: string) => Promise<string>;
  nameOf: (userId: string) => string;
  storage?: Pick<Storage, "getItem" | "setItem">;
  /** told about a ring so the app can show a browser notification */
  onRing?: (c: IncomingCall) => void;
}

export const RING_MS = 45_000;
const EMPTY_ROOM_MS = 8_000;
const MAX_HISTORY = 200;

const initial = (): CallState => ({ phase: "idle", active: null, incoming: null, peers: [], streams: [], localStream: null, history: [], error: "" });

export function createCallStore(deps: CallDeps) {
  const store: StoreApi<CallState> = createStore<CallState>(() => initial());
  const now = deps.now ?? Date.now;
  const getMedia = deps.getMedia ?? ((c) => navigator.mediaDevices.getUserMedia(c));
  const set = store.setState;
  const get = store.getState;
  let session: ReturnType<NonNullable<CallDeps["newSession"]>> | null = null;
  let me = "";
  let ringTimer: number | undefined;
  let emptyTimer: number | undefined;
  let incomingTimer: number | undefined;
  let attempt = 0; // guards against a late callback from a previous call

  const key = () => `spurt.calls.v1.${me}`;
  const storage = (): Pick<Storage, "getItem" | "setItem"> | null => {
    try {
      return deps.storage ?? window.localStorage;
    } catch {
      return null;
    }
  };
  const persist = () => {
    try {
      storage()?.setItem(key(), JSON.stringify(get().history.slice(0, MAX_HISTORY)));
    } catch {
      /* history is simply not kept */
    }
  };
  const addHistory = (r: Omit<CallRecord, "id">) => {
    set((s) => ({ history: [{ ...r, id: `c${r.at}-${Math.random().toString(36).slice(2, 7)}` }, ...s.history].slice(0, MAX_HISTORY) }));
    persist();
  };
  const clearTimers = () => {
    window.clearTimeout(ringTimer);
    window.clearTimeout(emptyTimer);
  };

  const sfuEvents = (token: number): SfuEvents => ({
    onRoster: (peers) => {
      if (token !== attempt) return;
      set({ peers });
      const a = get().active;
      if (!a) return;
      const others = peers.filter((p) => p.user !== me);
      if (others.length > 0 && !a.connectedAt) {
        clearTimers();
        set({ phase: "connected", active: { ...a, connectedAt: now() } });
      } else if (others.length === 0 && a.connectedAt) {
        void actions.end("remote"); // the other person left
      }
    },
    onStreams: (streams) => token === attempt && set({ streams }),
    onEnd: (reason) => token === attempt && void actions.end(reason === "failed" ? "failed" : "remote"),
  });

  const join = async (token: number, a: ActiveCall, ring: boolean): Promise<void> => {
    let media: MediaStream;
    try {
      media = await getMedia(mediaConstraints(a.video));
    } catch (e) {
      throw new Error(mediaErrorText(e));
    }
    if (token !== attempt) {
      media.getTracks().forEach((t) => t.stop());
      return;
    }
    let info: JoinInfo;
    try {
      info = await deps.api<JoinInfo>(`/api/chat/conversations/${encodeURIComponent(a.convId)}/call`, { method: "POST", body: { ring } });
    } catch (e) {
      media.getTracks().forEach((t) => t.stop());
      throw new Error(e instanceof Error && e.message ? (e as { code?: string }).code === "not_configured" ? "Calls aren't set up on this server yet" : e.message : "Couldn't start the call");
    }
    const s = (session = deps.newSession ? deps.newSession(sfuEvents(token)) : new SfuSession(sfuEvents(token)));
    try {
      await s.join(info, media, a.video);
    } catch (e) {
      media.getTracks().forEach((t) => t.stop());
      throw new Error(e instanceof JoinError ? e.message : "Couldn't connect the call");
    }
    if (token !== attempt) {
      s.leave();
      return;
    }
    set({ localStream: media });
  };

  const actions = {
    init(meId: string): () => void {
      me = meId;
      set({ ...initial(), history: load() });
      return deps.rt.onFrame((f) => actions.handleFrame(f));
    },

    async start(contactId: string, video = false): Promise<void> {
      if (get().phase !== "idle") throw new Error("You're already on a call");
      const token = ++attempt;
      const name = deps.nameOf(contactId);
      set({ phase: "starting", error: "", incoming: null, peers: [], streams: [], active: { convId: "", contactId, name, direction: "outgoing", video, startedAt: now(), connectedAt: null, muted: false, hold: false, camOn: video } });
      try {
        const convId = await deps.openDM(contactId);
        const a = { ...get().active!, convId };
        set({ active: a });
        await join(token, a, true);
        if (token !== attempt) return;
        set({ phase: "ringing" });
        ringTimer = window.setTimeout(() => void actions.end("noanswer"), RING_MS);
      } catch (e) {
        if (token === attempt) await actions.end("error", e instanceof Error ? e.message : "Couldn't start the call");
        throw e;
      }
    },

    async accept(withVideo = false): Promise<void> {
      const inc = get().incoming;
      if (!inc || get().phase !== "idle") return;
      window.clearTimeout(incomingTimer);
      const token = ++attempt;
      const a: ActiveCall = { convId: inc.convId, contactId: inc.contactId, name: inc.name, direction: "incoming", video: withVideo, startedAt: now(), connectedAt: null, muted: false, hold: false, camOn: withVideo };
      set({ phase: "connecting", incoming: null, active: a, peers: [], streams: [], error: "" });
      try {
        await join(token, a, false);
        if (token !== attempt) return;
        // the caller may have hung up while this phone was ringing: then nobody else is in the room
        emptyTimer = window.setTimeout(() => {
          const cur = get();
          if (token === attempt && cur.active && !cur.active.connectedAt) void actions.end("gone");
        }, EMPTY_ROOM_MS);
      } catch (e) {
        if (token === attempt) await actions.end("error", e instanceof Error ? e.message : "Couldn't join the call");
        throw e;
      }
    },

    decline(): void {
      const inc = get().incoming;
      if (!inc) return;
      window.clearTimeout(incomingTimer);
      addHistory({ contactId: inc.contactId, name: inc.name, direction: "missed", at: inc.at, seconds: 0, read: true });
      set({ incoming: null });
    },

    async end(why: "local" | "remote" | "failed" | "noanswer" | "gone" | "error" | EndReason = "local", message = ""): Promise<void> {
      const a = get().active;
      if (!a) return;
      attempt++;
      clearTimers();
      const s = session;
      session = null;
      try {
        s?.leave();
      } catch {
        /* already closed */
      }
      get().localStream?.getTracks().forEach((t) => t.stop());
      const seconds = a.connectedAt ? Math.max(1, Math.floor((now() - a.connectedAt) / 1000)) : 0;
      if (a.direction === "outgoing") {
        addHistory({ contactId: a.contactId, name: a.name, direction: "outgoing", at: a.startedAt, seconds, read: true });
      } else if (seconds > 0) {
        addHistory({ contactId: a.contactId, name: a.name, direction: "incoming", at: a.startedAt, seconds, read: true });
      } else {
        addHistory({ contactId: a.contactId, name: a.name, direction: "missed", at: a.startedAt, seconds: 0, read: true });
      }
      const text =
        message ||
        (why === "noanswer" ? `${a.name} didn't answer` : why === "gone" ? `${a.name} already hung up` : why === "failed" ? "The call lost its connection" : "");
      set({ phase: "idle", active: null, peers: [], streams: [], localStream: null, error: text });
    },

    toggleMute(): void {
      const a = get().active;
      if (!a) return;
      const muted = !a.muted;
      session?.setMicEnabled(!muted && !a.hold);
      set({ active: { ...a, muted } });
    },

    toggleHold(): void {
      const a = get().active;
      if (!a) return;
      const hold = !a.hold;
      session?.setMicEnabled(!hold && !a.muted);
      set({ active: { ...a, hold } });
    },

    toggleCamera(): void {
      const a = get().active;
      if (!a || !a.video) return;
      session?.setCameraEnabled(!a.camOn);
      set({ active: { ...a, camOn: !a.camOn } });
    },

    markRead(id?: string): void {
      set((s) => ({ history: s.history.map((r) => (!id || r.id === id ? { ...r, read: true } : r)) }));
      persist();
    },

    setHistory(updater: (h: CallRecord[]) => CallRecord[]): void {
      set((s) => ({ history: updater(s.history) }));
      persist();
    },

    clearError(): void {
      set({ error: "" });
    },

    handleFrame(f: Frame): void {
      if (f.t !== "call.started") return;
      const d = f.d as { convId?: string; kind?: string; userId?: string; name?: string; at?: string };
      if (!d.convId || !d.userId || d.userId === me) return;
      if (d.kind && d.kind !== "dm") return; // group and channel calls are announced through notifications
      const s = get();
      if (s.phase !== "idle" || s.incoming) return; // busy
      const inc: IncomingCall = { convId: d.convId, contactId: d.userId, name: d.name || deps.nameOf(d.userId), at: now() };
      set({ incoming: inc });
      deps.onRing?.(inc);
      window.clearTimeout(incomingTimer);
      incomingTimer = window.setTimeout(() => {
        const cur = get().incoming;
        if (cur && cur.convId === inc.convId) {
          addHistory({ contactId: inc.contactId, name: inc.name, direction: "missed", at: inc.at, seconds: 0, read: false });
          set({ incoming: null });
        }
      }, RING_MS);
    },
  };

  function load(): CallRecord[] {
    try {
      const raw = storage()?.getItem(key());
      const v = raw ? (JSON.parse(raw) as unknown) : [];
      return Array.isArray(v) ? (v as CallRecord[]).filter((r) => r && typeof r.id === "string" && typeof r.at === "number").slice(0, MAX_HISTORY) : [];
    } catch {
      return [];
    }
  }

  return { store, actions };
}

export type CallActions = ReturnType<typeof createCallStore>["actions"];

// The app-wide instance exists from the start (screens subscribe while rendering, before sign-in finishes wiring things up).
let ringHandler: ((c: IncomingCall) => void) | undefined;
/** Let the app react to a ring, e.g. with a browser notification while the tab is hidden. */
export const setRingHandler = (fn: ((c: IncomingCall) => void) | undefined): void => {
  ringHandler = fn;
};

const inst = createCallStore({
  api: realApi,
  rt: realRt,
  openDM: (uid) => chat.openDM(uid),
  nameOf: (uid) => chatStore.getState().users[uid]?.name ?? "Someone",
  onRing: (c) => ringHandler?.(c),
});
export const callStore = (): StoreApi<CallState> => inst.store;
export const calls = (): CallActions => inst.actions;
export const useCalls = <T,>(selector: (s: CallState) => T): T => useStore(inst.store, selector);
